import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { RoutingProfile, UserListItem } from "@repo/core/models"
import invariant from "tiny-invariant"

import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogBodyError,
  DialogBodySuccess,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { formatFullName } from "@/helpers/names"

import { UserGroups } from "../list/users-table"
import { useFetchRoutingProfiles, useMutationUpdateUser } from "../users-api"
import { RoutingProfilePicker } from "./routing-profile-picker"

type Props = {
  close: (value: boolean) => void
  user: UserListItem
}

type FormValues = {
  routingProfileId: string
}

export function DialogEditUser({ close, user }: Props) {
  const { t } = useTranslation()
  invariant(user.cognito_user_id)
  const mutation = useMutationUpdateUser(user.cognito_user_id)
  const { data: routingProfilesData } = useFetchRoutingProfiles()
  const routingProfiles = routingProfilesData?.data ?? []

  const form = useForm<FormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      routingProfileId: user.routing_profile_id || "",
    },
  })

  const hasChanged =
    user.routing_profile_id !== form.getValues().routingProfileId

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    const values = form.getValues()
    if (!values.routingProfileId) return
    mutation.mutate(values.routingProfileId)
  }

  return (
    <DialogContent isPending={mutation.isPending} className="max-w-2xl">
      <form onSubmit={handleSave}>
        <DialogHeader>
          <DialogTitle>{t("users.edit.title")}</DialogTitle>
        </DialogHeader>
        {(mutation.isIdle || mutation.isPending) && (
          <>
            <DialogBody>
              <UserForm
                form={form}
                user={user}
                routingProfiles={routingProfiles}
              />
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                isDisabled={mutation.isPending}
              >
                {t("global.button.cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={mutation.isPending}
                isDisabled={!hasChanged}
              >
                {t("global.button.save")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isSuccess && (
          <>
            <DialogBodySuccess>
              {t("users.edit.success", {
                name: formatFullName(user.first_name, user.last_name),
              })}
            </DialogBodySuccess>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(true)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isError && (
          <>
            <DialogBodyError>Unable to edit the user</DialogBodyError>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </form>
    </DialogContent>
  )
}

function UserForm({
  form,
  routingProfiles,
  user,
}: {
  form: ReturnType<typeof useForm<FormValues>>
  routingProfiles: RoutingProfile[]
  user: Props["user"]
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-6">
      <Form {...form}>
        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.name")}
          </FormLabel>
          <div>{formatFullName(user.first_name, user.last_name)}</div>
        </FormItem>

        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.role")}
          </FormLabel>
          <div>
            <UserGroups groups={user.groups} />
          </div>
        </FormItem>

        <FormItem>
          <FormLabel className="text-muted-foreground">
            {t("users.list.email")}
          </FormLabel>
          <div>{user.email}</div>
        </FormItem>

        <FormField
          control={form.control}
          name="routingProfileId"
          label={t("users.edit.routingProfile")}
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel className="text-muted-foreground">
                {t("users.edit.routingProfile")}
              </FormLabel>
              <FormControl>
                <RoutingProfilePicker
                  {...field}
                  routingProfiles={routingProfiles}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </Form>
    </div>
  )
}
